import { useCallback, useState } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

import Button, { ButtonTypes } from "../../../../components/Button";
import { SearchInput } from "../../../../components/Input/SearchInput";
import { StringOptions } from "../../../../components/Select";
import { TransactionType } from "../../../../utils";
import FilterDropdown from "./FilterDropdown";
import PeriodSelector from "./PeriodSelector";
import { ALL_OPTION, ALL_VALUE, TYPE_OPTIONS } from "./constants";
import {
  AdvancedRow,
  Card,
  Chevron,
  ClearFiltersIcon,
  ClearSlot,
  Count,
  FiltersRow,
  MoreBadge,
  PeriodControl,
  PeriodLabel,
  PeriodRow,
  Pill,
  Pills,
} from "./styles";
import { TransactionsFiltersProps } from "./types";

const TransactionsFilters: React.FC<TransactionsFiltersProps> = ({
  monthOptions,
  activeMonth,
  rangeValue,
  rangeOptions,
  yearOptions,
  yearValue,
  monthChoiceOptions,
  monthChoiceValue,
  showYear,
  showMonthChoice,
  onSelectMonth,
  onSelectRange,
  onSelectYear,
  onSelectMonthChoice,
  filteredCount,
  totalCount,
  values,
  typeOptions,
  showCategoryFilter,
  categoryOptions,
  accountOptions,
  sortOptions,
  onValuesChange,
  cashbackBuysLabel = null,
  onClearCashbackBuys,
}) => {
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onValuesChange({ searchBy: event.target.value });
    },
    [onValuesChange],
  );

  const handleClearSearch = useCallback(() => {
    onValuesChange({ searchBy: "" });
  }, [onValuesChange]);

  const handleType = useCallback(
    (option: StringOptions) => {
      onValuesChange({ type: option });
    },
    [onValuesChange],
  );

  const handleCategory = useCallback(
    (option: StringOptions) => {
      onValuesChange({ category: option.value === ALL_VALUE ? null : option });
    },
    [onValuesChange],
  );

  const handleAccount = useCallback(
    (option: StringOptions) => {
      onValuesChange({ account: option.value === ALL_VALUE ? null : option });
    },
    [onValuesChange],
  );

  const handleSort = useCallback(
    (option: StringOptions) => {
      onValuesChange({ sort: option });
    },
    [onValuesChange],
  );

  const handleClearFilters = useCallback(() => {
    onValuesChange({
      searchBy: "",
      type: TYPE_OPTIONS[0],
      category: null,
      account: null,
    });
  }, [onValuesChange]);

  const toggleMore = useCallback(() => {
    setIsMoreOpen((prev) => !prev);
  }, []);

  const isTypeActive = values.type.value !== TransactionType.ALL;
  const categoryValue = values.category ?? ALL_OPTION;
  const accountValue = values.account ?? ALL_OPTION;
  const formattedFiltered = filteredCount.toLocaleString("en-US");
  const formattedTotal = totalCount.toLocaleString("en-US");
  const countLabel = `${formattedFiltered} of ${formattedTotal} transactions`;
  const hasActiveFilters =
    values.searchBy !== "" ||
    isTypeActive ||
    values.category !== null ||
    values.account !== null;

  // Category and Account live behind the toggle; count them so a collapsed
  // panel still advertises how many secondary filters are in play.
  const advancedActiveCount =
    (values.category !== null ? 1 : 0) + (values.account !== null ? 1 : 0);
  const hasAdvancedActive = advancedActiveCount > 0;
  // Never hide an active filter: pin the panel open while one is set, and let
  // the user open it manually otherwise.
  const isAdvancedOpen = isMoreOpen || hasAdvancedActive;

  return (
    <Card aria-label="Transaction filters">
      <PeriodRow>
        <PeriodLabel>Period</PeriodLabel>
        <Count>{countLabel}</Count>
        <PeriodControl>
          <PeriodSelector
            monthOptions={monthOptions}
            activeMonth={activeMonth}
            rangeValue={rangeValue}
            rangeOptions={rangeOptions}
            yearOptions={yearOptions}
            yearValue={yearValue}
            monthChoiceOptions={monthChoiceOptions}
            monthChoiceValue={monthChoiceValue}
            showYear={showYear}
            showMonthChoice={showMonthChoice}
            onSelectMonth={onSelectMonth}
            onSelectRange={onSelectRange}
            onSelectYear={onSelectYear}
            onSelectMonthChoice={onSelectMonthChoice}
          />
        </PeriodControl>
      </PeriodRow>

      <FiltersRow>
        <SearchInput
          value={values.searchBy}
          onChange={handleSearch}
          onClear={handleClearSearch}
          placeholder="Search notes"
          aria-label="Search transactions"
        />

        <Pills>
          <FilterDropdown
            label="Type"
            value={values.type}
            options={typeOptions}
            isActive={isTypeActive}
            onChange={handleType}
            testId="filter-type"
          />
          {cashbackBuysLabel && (
            <Pill
              type="button"
              $active
              aria-label={`Remove filter: ${cashbackBuysLabel}`}
              onClick={onClearCashbackBuys}
              data-testid="filter-cashback-buys"
            >
              {cashbackBuysLabel}
              <CloseRoundedIcon fontSize="small" />
            </Pill>
          )}
        </Pills>

        <Pill
          type="button"
          $active={hasAdvancedActive}
          aria-haspopup="true"
          aria-expanded={isAdvancedOpen}
          aria-controls="advanced-filters"
          onClick={toggleMore}
          data-testid="more-filters"
        >
          <TuneRoundedIcon fontSize="small" />
          Filters
          {advancedActiveCount > 0 && (
            <MoreBadge>{advancedActiveCount}</MoreBadge>
          )}
          <Chevron $isOpen={isAdvancedOpen} />
        </Pill>
      </FiltersRow>

      {isAdvancedOpen && (
        <AdvancedRow id="advanced-filters">
          {showCategoryFilter && (
            <FilterDropdown
              label="Category"
              value={categoryValue}
              options={categoryOptions}
              isActive={values.category !== null}
              onChange={handleCategory}
              testId="filter-category"
            />
          )}
          <FilterDropdown
            label="Account"
            value={accountValue}
            options={accountOptions}
            isActive={values.account !== null}
            onChange={handleAccount}
            testId="filter-account"
          />
          <FilterDropdown
            label="Sort"
            value={values.sort}
            options={sortOptions}
            isActive={false}
            onChange={handleSort}
            testId="filter-sort"
          />

          {hasActiveFilters && (
            <ClearSlot>
              <Button
                type={ButtonTypes.clear}
                onClick={handleClearFilters}
                aria-label="Clear all filters"
                data-testid="clear-filters"
              >
                <ClearFiltersIcon />
                <span>Clear filters</span>
              </Button>
            </ClearSlot>
          )}
        </AdvancedRow>
      )}
    </Card>
  );
};

export default TransactionsFilters;
